const VISITOR_ID_KEY = "rw-beta-visitor-id";
const HAS_VISITED_KEY = "rw-beta-has-visited";
const SESSION_START_KEY = "rw-beta-session-start";
const EXPLORED_MODULES_KEY = "rw-beta-explored-modules";
const ENGAGED_SENT_KEY = "rw-beta-metrics:engaged_visit";
const SESSION_ABANDONED_KEY = "rw-beta-modal-abandoned";
const POST_ABANDON_EXPLORE_SENT_KEY = "rw-beta-metrics:post_abandon_explore";
/** Survives sessions — set on abandon; consumed once on a later return visit. */
const PENDING_RETURN_AFTER_ABANDON_KEY = "rw-beta-pending-return-after-abandon";
const VISITOR_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type VisitorContext = {
  visitorId: string;
  isReturning: boolean;
};

export function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return "unknown";
  let visitorId = "";
  try {
    const incoming = new URLSearchParams(window.location.search).get("visitor_id") ?? "";
    visitorId = VISITOR_ID_RE.test(incoming)
      ? incoming.toLowerCase()
      : localStorage.getItem(VISITOR_ID_KEY) ?? "";
    if (VISITOR_ID_RE.test(visitorId)) localStorage.setItem(VISITOR_ID_KEY, visitorId);
  } catch {
    visitorId = "";
  }

  if (!VISITOR_ID_RE.test(visitorId)) {
    visitorId = crypto.randomUUID();
    try {
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    } catch {
      /* storage unavailable */
    }
  }

  return visitorId;
}

export function getVisitorContext(): VisitorContext {
  if (typeof window === "undefined") {
    return { visitorId: "unknown", isReturning: false };
  }

  const visitorId = getOrCreateVisitorId();
  const isReturning = (() => {
    try {
      return localStorage.getItem(HAS_VISITED_KEY) === "1";
    } catch {
      return false;
    }
  })();

  try {
    localStorage.setItem(HAS_VISITED_KEY, "1");
  } catch {
    /* storage unavailable */
  }

  return { visitorId, isReturning };
}

export function getSessionElapsedMs(): number {
  if (typeof window === "undefined") return 0;

  let startRaw = "";
  try {
    startRaw = sessionStorage.getItem(SESSION_START_KEY) ?? "";
  } catch {
    return 0;
  }

  if (!startRaw) {
    const now = String(Date.now());
    try {
      sessionStorage.setItem(SESSION_START_KEY, now);
    } catch {
      /* storage unavailable */
    }
    return 0;
  }

  const start = Number(startRaw);
  if (!Number.isFinite(start) || start <= 0) return 0;
  return Math.max(0, Date.now() - start);
}

function readExploredModules(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = sessionStorage.getItem(EXPLORED_MODULES_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string" && id.length > 0));
  } catch {
    return new Set();
  }
}

function writeExploredModules(modules: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(EXPLORED_MODULES_KEY, JSON.stringify([...modules]));
  } catch {
    /* storage unavailable */
  }
}

export function markModuleExplored(moduleId: string): void {
  const trimmed = moduleId.trim();
  if (!trimmed) return;
  const modules = readExploredModules();
  modules.add(trimmed);
  writeExploredModules(modules);
}

export function getExploreSignupContext(): {
  hadModuleZoom: boolean;
  exploredModulesCount: number;
} {
  const modules = readExploredModules();
  return {
    hadModuleZoom: modules.size > 0,
    exploredModulesCount: modules.size,
  };
}

export function hasSentEngagedVisit(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return sessionStorage.getItem(ENGAGED_SENT_KEY) === "1";
  } catch {
    return false;
  }
}

export function markEngagedVisitSent(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(ENGAGED_SENT_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

/** Mark that this session abandoned the join modal (session + durable return flag). */
export function markModalAbandoned(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_ABANDONED_KEY, "1");
  } catch {
    /* storage unavailable */
  }
  try {
    localStorage.setItem(PENDING_RETURN_AFTER_ABANDON_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

export function sessionAbandonedModal(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(SESSION_ABANDONED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * First module zoom after an abandon in this session.
 * Returns module_id to emit once; does not replace module_zoom.
 */
export function takePostAbandonExploreModuleId(moduleId: string): string | null {
  const trimmed = moduleId.trim();
  if (!trimmed || !sessionAbandonedModal()) return null;
  if (typeof window === "undefined") return null;
  try {
    if (sessionStorage.getItem(POST_ABANDON_EXPLORE_SENT_KEY) === "1") return null;
    sessionStorage.setItem(POST_ABANDON_EXPLORE_SENT_KEY, "1");
  } catch {
    return trimmed;
  }
  return trimmed;
}

/**
 * Later visit after a prior abandon (not same-session refresh).
 * Consumed once so we count the return pattern without double-counting forever.
 */
export function takeReturnAfterAbandon(isReturning: boolean): boolean {
  if (typeof window === "undefined" || !isReturning) return false;
  // Same tab/session refresh after abandon is not a "revisit".
  if (sessionAbandonedModal()) return false;
  try {
    if (localStorage.getItem(PENDING_RETURN_AFTER_ABANDON_KEY) !== "1") return false;
    localStorage.removeItem(PENDING_RETURN_AFTER_ABANDON_KEY);
    return true;
  } catch {
    return false;
  }
}
